/**
 * /app/marketing — Marketing source ROI dashboard.
 *
 * Two stacked sections:
 *   1. Spend log — quick-add form + table of logged spend records
 *   2. ROI report — per-source aggregated stats (leads, contracts, closed,
 *      revenue, cost-per-lead, cost-per-deal, ROI%)
 *
 * Optional period filter (YYYY-MM) to scope the analysis to a single month.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, DollarSign, TrendingUp, Calendar } from "lucide-react";
import { api, fmtMoney } from "@/lib/api";
import { track } from "@/lib/analytics";

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Marketing() {
  const [period, setPeriod] = useState("");  // "" = all-time
  const [spend, setSpend] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: r }] = await Promise.all([
        api.get("/marketing/spend"),
        api.get("/marketing/roi", { params: period ? { period } : {} }),
      ]);
      setSpend(s.spend || []);
      setReport(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load marketing data");
    }
    setLoading(false);
  }, [period]);

  useEffect(() => { track("marketing_page_view"); load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="marketing-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">ROI & attribution</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Marketing</h1>
          <p className="text-[var(--ink-2)] mt-1">Log what you spend. See what closes. Decide where to double down.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-3)]">Period</label>
          <input
            type="month"
            className="input w-44"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            data-testid="marketing-period"
          />
          {period && (
            <button type="button" className="btn-ghost text-sm" onClick={() => setPeriod("")} data-testid="marketing-clear-period">
              All-time
            </button>
          )}
        </div>
      </div>

      {loading || !report ? (
        <div className="card text-center py-16 text-[var(--ink-2)]">
          <Loader2 size={20} className="animate-spin mx-auto mb-3" /> Loading…
        </div>
      ) : (
        <>
          <TotalsStrip totals={report.totals} period={report.period} />
          <RoiTable rows={report.rows} />
          <SpendSection spend={spend} onChange={load} />
        </>
      )}
    </div>
  );
}

function TotalsStrip({ totals, period }) {
  return (
    <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-3" data-testid="marketing-totals">
      <Metric icon={DollarSign} label="Spend" value={fmtMoney(totals.spend)} tone="amber" />
      <Metric label="Leads" value={totals.leads} />
      <Metric label="Closed deals" value={totals.closed} />
      <Metric icon={TrendingUp} label="Revenue" value={fmtMoney(totals.revenue)} tone="emerald" />
      <Metric label="ROI" value={totals.roi_pct == null ? "—" : `${totals.roi_pct}%`} tone={totals.roi_pct > 0 ? "emerald" : "neutral"} />
    </div>
  );
}

function Metric({ icon: Icon, label, value, tone }) {
  const toneMap = {
    amber:   "border-l-amber-500",
    emerald: "border-l-emerald-500",
    neutral: "border-l-[var(--ink-3)]",
  };
  return (
    <div className={"card border-l-4 p-4 " + (toneMap[tone] || "border-l-[var(--line)]")}>
      <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-bold mb-1 flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className="font-display font-black text-2xl tracking-tighter">{value}</div>
    </div>
  );
}

function RoiTable({ rows }) {
  return (
    <div className="card overflow-x-auto" data-testid="marketing-roi-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-[var(--ink-3)] border-b border-[var(--line)]">
            <Th>Source</Th>
            <Th align="right">Spend</Th>
            <Th align="right">Leads</Th>
            <Th align="right">Contracts</Th>
            <Th align="right">Closed</Th>
            <Th align="right">Revenue</Th>
            <Th align="right">CPL</Th>
            <Th align="right">CPD</Th>
            <Th align="right">ROI%</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={9} className="p-8 text-center text-[var(--ink-3)]">No data yet. Log some spend below to start tracking ROI.</td></tr>
          ) : rows.map((r) => (
            <tr key={r.source} className="border-b border-[var(--line)] hover:bg-[var(--bg-2)]" data-testid={`marketing-row-${r.source}`}>
              <Td><span className="font-medium text-[var(--ink)]">{r.source}</span></Td>
              <Td align="right">{fmtMoney(r.spend)}</Td>
              <Td align="right">{r.leads}</Td>
              <Td align="right">{r.contracts}</Td>
              <Td align="right">{r.closed}</Td>
              <Td align="right" className="font-semibold">{fmtMoney(r.revenue)}</Td>
              <Td align="right">{r.cost_per_lead ? fmtMoney(r.cost_per_lead) : "—"}</Td>
              <Td align="right">{r.cost_per_deal ? fmtMoney(r.cost_per_deal) : "—"}</Td>
              <Td align="right" className={r.roi_pct == null ? "" : (r.roi_pct > 0 ? "text-emerald-600 font-bold" : "text-red-600 font-bold")}>
                {r.roi_pct == null ? "—" : `${r.roi_pct}%`}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`p-3 font-bold ${align === "right" ? "text-right" : "text-left"}`}>{children}</th>;
}
function Td({ children, align = "left", className = "" }) {
  return <td className={`p-3 ${align === "right" ? "text-right tabular-nums" : ""} ${className}`}>{children}</td>;
}

function SpendSection({ spend, onChange }) {
  const [source, setSource] = useState("");
  const [period, setPeriod] = useState(currentPeriod());
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!source.trim() || !amount) return;
    setSaving(true);
    try {
      await api.post("/marketing/spend", {
        source: source.trim(),
        period,
        amount: parseFloat(amount),
      });
      setSource(""); setAmount("");
      toast.success("Spend logged");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log spend");
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this spend record?")) return;
    try {
      await api.delete(`/marketing/spend/${id}`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="card p-5" data-testid="marketing-spend-section">
      <div className="font-display font-bold mb-3 text-lg">Spend log</div>
      <form onSubmit={submit} className="flex flex-wrap items-center gap-2 mb-4">
        <input
          className="input flex-1 min-w-[200px]"
          placeholder="Source (e.g. Probate List, PPC, Direct Mail)"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          list="marketing-sources"
          data-testid="spend-source"
        />
        <datalist id="marketing-sources">
          <option value="Probate List" /><option value="Pre-Foreclosure" />
          <option value="PPC" /><option value="Direct Mail" /><option value="Carrot SEO" />
          <option value="Driving for Dollars" /><option value="Bandit Signs" />
          <option value="Facebook Ads" /><option value="Referral" />
        </datalist>
        <input
          type="month"
          className="input w-44"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          data-testid="spend-period"
          required
        />
        <input
          type="number"
          step="0.01"
          min="0"
          className="input w-32"
          placeholder="$"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="spend-amount"
        />
        <button
          type="submit"
          className="btn-money"
          disabled={saving || !source.trim() || !amount}
          data-testid="spend-submit"
        >
          <Plus size={14} /> Log spend
        </button>
      </form>
      {spend.length === 0 ? (
        <div className="text-sm text-[var(--ink-3)] text-center py-4">No spend logged yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[var(--ink-3)] border-b border-[var(--line)]">
                <Th>Period</Th><Th>Source</Th><Th align="right">Amount</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {spend.map((s) => (
                <tr key={s.id} className="border-b border-[var(--line)]">
                  <Td><Calendar size={11} className="inline mr-1" /> {s.period}</Td>
                  <Td>{s.source}</Td>
                  <Td align="right" className="tabular-nums font-semibold">{fmtMoney(s.amount)}</Td>
                  <Td align="right">
                    <button type="button" className="btn-ghost text-red-600" onClick={() => remove(s.id)} aria-label="Delete">
                      <Trash2 size={12} />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
